import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import './App.css';
import './ExperimentsView.css';
import {
    IExperiment,
    ExperimentFormData,
    TypeConfig,
    EXPERIMENT_TYPES,
    PROVIDER_OPTIONS,
    VALIDATION_FIELDS,
    getExperimentTypeIcon,
    isLLMExperimentType,
    isEngineExperimentType,
    SearchEngineConfig,
    ScrapeEngineConfig,
    DownloadEngineConfig,
    EngineConfig
} from './types/experiment';

// Model options per provider
const MODEL_OPTIONS: Record<string, string[]> = {
    azure_openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-35-turbo'],
    openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
    anthropic: ['claude-3-5-sonnet-latest', 'claude-3-5-haiku-latest', 'claude-3-opus-latest'],
    aws_bedrock: ['anthropic.claude-3-sonnet', 'anthropic.claude-3-haiku', 'amazon.titan-text-express']
};

// Search engine options
const SEARCH_ENGINE_OPTIONS = ['brave', 'google', 'bing'];

// Crawler engine options
const CRAWLER_ENGINE_OPTIONS = ['firecrawl', 'playwright', 'requests'];

const DEFAULT_FORM_DATA: ExperimentFormData = {
    experiment_id: '',
    type: '',
    provider: '',
    model: '',
    endpoint: '',
    tags: [],
    typeConfig: {},
    temperature: 0.7,
    maxTokens: 4096,
    prompt: '',
    json_schema: '',
    engineConfig: undefined
};

const ExperimentCreateEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { token, authLoaded, user } = useAuth();
    const { t } = useTranslation();

    const isEditMode = Boolean(id);
    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;

    const [formData, setFormData] = useState<ExperimentFormData>(DEFAULT_FORM_DATA);
    const [originalData, setOriginalData] = useState<ExperimentFormData | null>(null);
    const [loading, setLoading] = useState<boolean>(isEditMode);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [tagsInput, setTagsInput] = useState<string>('');

    // Fetch experiment data in edit mode
    const fetchExperiment = useCallback(async () => {
        if (!id) return;

        try {
            setLoading(true);
            const headers: Record<string, string> = {
                'x-api-key': apiKey
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await axios.get<{ experiment: IExperiment }>(`${apiUrl}/admin/experiments/${id}`, { headers });
            const exp = response.data.experiment;

            // Map experiment to form data
            const mapped: ExperimentFormData = {
                experiment_id: exp.experiment_id,
                type: exp.experiment_data?.type || '',
                provider: exp.experiment_data?.provider || '',
                model: exp.experiment_data?.model || '',
                endpoint: exp.experiment_data?.endpoint || '',
                tags: exp.experiment_data?.tags || [],
                typeConfig: {},
                temperature: 0.7,
                maxTokens: exp.experiment_data?.context_size || 4096,
                prompt: exp.experiment_data?.prompt || '',
                json_schema: exp.experiment_data?.json_schema || ''
            };

            setFormData(mapped);
            setOriginalData(mapped);
            setTagsInput(mapped.tags.join(', '));
            setError(null);
        } catch (err: any) {
            console.error('Error fetching experiment:', err);
            if (err.response?.status === 404) {
                setError(t('experiments.notFound'));
            } else {
                setError(t('experiments.loadError'));
            }
        } finally {
            setLoading(false);
        }
    }, [id, token, apiKey, apiUrl, t]);

    useEffect(() => {
        if (!authLoaded) return;
        if (isEditMode) {
            fetchExperiment();
        }
    }, [authLoaded, isEditMode, fetchExperiment]);

    // Track changes
    useEffect(() => {
        if (originalData) {
            const changed = JSON.stringify(formData) !== JSON.stringify(originalData);
            setHasChanges(changed);
        } else {
            // In create mode, check if any field has been filled
            setHasChanges(
                formData.experiment_id !== '' ||
                formData.type !== '' ||
                formData.provider !== ''
            );
        }
    }, [formData, originalData]);

    // Handle field changes
    const handleChange = useCallback((field: keyof ExperimentFormData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    }, [validationErrors]);

    // Handle type config changes
    const handleTypeConfigChange = useCallback((field: keyof TypeConfig, value: any) => {
        setFormData(prev => ({
            ...prev,
            typeConfig: {
                ...prev.typeConfig,
                [field]: value
            }
        }));
    }, []);

    // Handle engine config changes
    const handleEngineConfigChange = useCallback((field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            engineConfig: {
                ...prev.engineConfig,
                [field]: value
            } as EngineConfig
        }));
    }, []);

    // Handle tags input
    const handleTagsChange = useCallback((value: string) => {
        setTagsInput(value);
        const tags = value.split(',').map(t => t.trim()).filter(t => t.length > 0);
        handleChange('tags', tags);
    }, [handleChange]);

    // Handle provider change (reset model when provider changes)
    const handleProviderChange = useCallback((provider: string) => {
        handleChange('provider', provider);
        handleChange('model', ''); // Reset model when provider changes
    }, [handleChange]);

    // Validate form
    const validateForm = useCallback((): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.experiment_id || formData.experiment_id.trim().length === 0) {
            errors.experiment_id = t('experiments.form.validation.experimentIdRequired');
        }

        if (!formData.type) {
            errors.type = t('experiments.form.validation.typeRequired');
        }

        // Only require provider and model for LLM-based experiments
        if (isLLMExperimentType(formData.type)) {
            if (!formData.provider) {
                errors.provider = t('experiments.form.validation.providerRequired');
            }

            if (!formData.model) {
                errors.model = t('experiments.form.validation.modelRequired');
            }
        }

        // Validate engine config for engine-based experiments
        if (isEngineExperimentType(formData.type)) {
            const engineConfig = formData.engineConfig;
            if (formData.type === 'Search' && !(engineConfig as SearchEngineConfig)?.search_engine) {
                errors.engineConfig = t('experiments.form.validation.searchEngineRequired');
            }
            if (formData.type === 'Scrape' && !(engineConfig as ScrapeEngineConfig)?.crawler_engine) {
                errors.engineConfig = t('experiments.form.validation.crawlerEngineRequired');
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData, t]);

    // Save experiment
    const handleSave = useCallback(async (asDraft: boolean = false) => {
        if (!validateForm()) return;

        setSaving(true);
        setError(null);

        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const isEngine = isEngineExperimentType(formData.type);

            const payload = {
                experiment_id: formData.experiment_id.trim(),
                experiment_data: {
                    type: formData.type,
                    // LLM-specific fields (only for LLM experiments)
                    provider: isEngine ? undefined : formData.provider,
                    model: isEngine ? undefined : formData.model,
                    endpoint: isEngine ? undefined : (formData.endpoint || undefined),
                    prompt: isEngine ? undefined : (formData.prompt || undefined),
                    json_schema: isEngine ? undefined : (formData.json_schema || undefined),
                    context_size: isEngine ? undefined : formData.maxTokens,
                    reasoning: isEngine ? undefined : false,
                    strict: isEngine ? undefined : false,
                    // Engine-specific fields (only for engine experiments)
                    engine_config: isEngine ? formData.engineConfig : undefined,
                    // Common fields
                    tags: formData.tags,
                    status: asDraft ? 'draft' : 'pending'
                },
                type_config: formData.typeConfig
            };

            if (isEditMode && id) {
                await axios.put(`${apiUrl}/admin/experiments/${id}`, payload, { headers });
            } else {
                await axios.post(`${apiUrl}/admin/experiments`, payload, { headers });
            }

            // Navigate back to experiments list
            const params = searchParams.toString();
            navigate(`/experiments${params ? `?${params}` : ''}`);
        } catch (err: any) {
            console.error('Error saving experiment:', err);
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError(isEditMode ? t('experiments.form.updateError') : t('experiments.form.createError'));
            }
        } finally {
            setSaving(false);
        }
    }, [formData, isEditMode, id, token, apiKey, apiUrl, searchParams, navigate, validateForm, t]);

    // Handle back navigation
    const handleBack = useCallback(() => {
        if (hasChanges) {
            if (window.confirm(t('experiments.form.unsavedChanges'))) {
                const params = searchParams.toString();
                navigate(`/experiments${params ? `?${params}` : ''}`);
            }
        } else {
            const params = searchParams.toString();
            navigate(`/experiments${params ? `?${params}` : ''}`);
        }
    }, [hasChanges, searchParams, navigate, t]);

    // Available models for selected provider
    const availableModels = formData.provider ? MODEL_OPTIONS[formData.provider] || [] : [];

    // Render type-specific configuration fields
    const renderTypeConfig = () => {
        switch (formData.type) {
            case 'Metadata Extraction':
                return (
                    <div className="form-section">
                        <h3>
                            <span className="material-icons">description</span>
                            {t('experiments.form.metadataExtraction.title')}
                        </h3>
                        <div className="form-group">
                            <label>{t('experiments.form.metadataExtraction.fieldsToExtract')}</label>
                            <div className="checkbox-group">
                                {VALIDATION_FIELDS.map(field => (
                                    <label key={field} className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={formData.typeConfig.fieldsToExtract?.includes(field) || false}
                                            onChange={(e) => {
                                                const current = formData.typeConfig.fieldsToExtract || [];
                                                const updated = e.target.checked
                                                    ? [...current, field]
                                                    : current.filter(f => f !== field);
                                                handleTypeConfigChange('fieldsToExtract', updated);
                                            }}
                                        />
                                        <span>{t(`experiments.validation.fields.${field}`)}</span>
                                    </label>
                                ))}
                            </div>
                            <small>{t('experiments.form.metadataExtraction.fieldsHelp')}</small>
                        </div>
                        <div className="form-group">
                            <label>{t('experiments.form.metadataExtraction.customPrompt')}</label>
                            <textarea
                                rows={4}
                                value={formData.typeConfig.customPrompt || ''}
                                onChange={(e) => handleTypeConfigChange('customPrompt', e.target.value)}
                                placeholder={t('experiments.form.metadataExtraction.customPromptPlaceholder')}
                            />
                        </div>
                    </div>
                );

            case 'Validation':
            case 'validation_llm_judge':
                return (
                    <div className="form-section">
                        <h3>
                            <span className="material-icons">fact_check</span>
                            {t('experiments.form.validationConfig.title')}
                        </h3>
                        <div className="form-group">
                            <label>{t('experiments.form.validationConfig.groundTruthSource')}</label>
                            <select
                                value={formData.typeConfig.groundTruthSource || 'mongodb'}
                                onChange={(e) => handleTypeConfigChange('groundTruthSource', e.target.value)}
                            >
                                <option value="mongodb">{t('experiments.form.validationConfig.mongodb')}</option>
                                <option value="csv">{t('experiments.form.validationConfig.csv')}</option>
                                <option value="manual">{t('experiments.form.validationConfig.manual')}</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>{t('experiments.form.validationConfig.confidenceThreshold')}</label>
                            <input
                                type="number"
                                min="0"
                                max="1"
                                step="0.05"
                                value={formData.typeConfig.confidenceThreshold || 0.7}
                                onChange={(e) => handleTypeConfigChange('confidenceThreshold', parseFloat(e.target.value))}
                            />
                            <small>{t('experiments.form.validationConfig.confidenceHelp')}</small>
                        </div>
                        <div className="form-group">
                            <label>{t('experiments.form.validationConfig.matchingMode')}</label>
                            <select
                                value={formData.typeConfig.matchingMode || 'fuzzy'}
                                onChange={(e) => handleTypeConfigChange('matchingMode', e.target.value)}
                            >
                                <option value="exact">{t('experiments.form.validationConfig.exact')}</option>
                                <option value="fuzzy">{t('experiments.form.validationConfig.fuzzy')}</option>
                                <option value="semantic">{t('experiments.form.validationConfig.semantic')}</option>
                            </select>
                        </div>
                    </div>
                );

            case 'Classification':
                return (
                    <div className="form-section">
                        <h3>
                            <span className="material-icons">category</span>
                            {t('experiments.form.classification.title')}
                        </h3>
                        <div className="form-group">
                            <label>{t('experiments.form.classification.categories')}</label>
                            <input
                                type="text"
                                value={formData.typeConfig.categories?.join(', ') || ''}
                                onChange={(e) => {
                                    const categories = e.target.value.split(',').map(c => c.trim()).filter(c => c.length > 0);
                                    handleTypeConfigChange('categories', categories);
                                }}
                                placeholder={t('experiments.form.classification.categoriesPlaceholder')}
                            />
                            <small>{t('experiments.form.classification.categoriesHelp')}</small>
                        </div>
                        <div className="form-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={formData.typeConfig.multiLabel || false}
                                    onChange={(e) => handleTypeConfigChange('multiLabel', e.target.checked)}
                                />
                                <span>{t('experiments.form.classification.multiLabel')}</span>
                            </label>
                            <small>{t('experiments.form.classification.multiLabelHelp')}</small>
                        </div>
                    </div>
                );

            case 'Inference':
                return (
                    <div className="form-section">
                        <h3>
                            <span className="material-icons">psychology</span>
                            {t('experiments.form.inference.title')}
                        </h3>
                        <div className="form-group">
                            <label>{t('experiments.form.inference.inferenceType')}</label>
                            <input
                                type="text"
                                value={formData.typeConfig.inferenceType || ''}
                                onChange={(e) => handleTypeConfigChange('inferenceType', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('experiments.form.inference.outputFormat')}</label>
                            <select
                                value={formData.typeConfig.outputFormat || 'text'}
                                onChange={(e) => handleTypeConfigChange('outputFormat', e.target.value)}
                            >
                                <option value="text">{t('experiments.form.inference.textOutput')}</option>
                                <option value="json">{t('experiments.form.inference.jsonOutput')}</option>
                                <option value="structured">{t('experiments.form.inference.structuredOutput')}</option>
                            </select>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // Render engine-specific configuration fields
    const renderEngineConfig = () => {
        const engineConfig = formData.engineConfig || {};

        switch (formData.type) {
            case 'Search':
                return (
                    <div className="form-section">
                        <h3>
                            <span className="material-icons">search</span>
                            {t('experiments.form.engineConfig.search.title')}
                        </h3>
                        <div className="form-group">
                            <label htmlFor="search_engine">
                                {t('experiments.form.engineConfig.search.searchEngine')} <span className="required">*</span>
                            </label>
                            <select
                                id="search_engine"
                                value={(engineConfig as SearchEngineConfig).search_engine || ''}
                                onChange={(e) => handleEngineConfigChange('search_engine', e.target.value)}
                            >
                                <option value="">{t('experiments.form.engineConfig.search.selectEngine')}</option>
                                {SEARCH_ENGINE_OPTIONS.map(engine => (
                                    <option key={engine} value={engine}>
                                        {engine.charAt(0).toUpperCase() + engine.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="max_results">
                                {t('experiments.form.engineConfig.search.maxResults')}
                            </label>
                            <input
                                id="max_results"
                                type="number"
                                min="1"
                                max="100"
                                value={(engineConfig as SearchEngineConfig).max_results || 10}
                                onChange={(e) => handleEngineConfigChange('max_results', parseInt(e.target.value))}
                            />
                            <small>{t('experiments.form.engineConfig.search.maxResultsHelp')}</small>
                        </div>
                    </div>
                );

            case 'Scrape':
                return (
                    <div className="form-section">
                        <h3>
                            <span className="material-icons">language</span>
                            {t('experiments.form.engineConfig.scrape.title')}
                        </h3>
                        <div className="form-group">
                            <label htmlFor="crawler_engine">
                                {t('experiments.form.engineConfig.scrape.crawlerEngine')} <span className="required">*</span>
                            </label>
                            <select
                                id="crawler_engine"
                                value={(engineConfig as ScrapeEngineConfig).crawler_engine || ''}
                                onChange={(e) => handleEngineConfigChange('crawler_engine', e.target.value)}
                            >
                                <option value="">{t('experiments.form.engineConfig.scrape.selectEngine')}</option>
                                {CRAWLER_ENGINE_OPTIONS.map(engine => (
                                    <option key={engine} value={engine}>
                                        {engine.charAt(0).toUpperCase() + engine.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="scrape_batch_size">
                                {t('experiments.form.engineConfig.scrape.batchSize')}
                            </label>
                            <input
                                id="scrape_batch_size"
                                type="number"
                                min="1"
                                max="100"
                                value={(engineConfig as ScrapeEngineConfig).batch_size || 10}
                                onChange={(e) => handleEngineConfigChange('batch_size', parseInt(e.target.value))}
                            />
                            <small>{t('experiments.form.engineConfig.scrape.batchSizeHelp')}</small>
                        </div>
                    </div>
                );

            case 'Download':
                return (
                    <div className="form-section">
                        <h3>
                            <span className="material-icons">download</span>
                            {t('experiments.form.engineConfig.download.title')}
                        </h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="download_batch_size">
                                    {t('experiments.form.engineConfig.download.batchSize')}
                                </label>
                                <input
                                    id="download_batch_size"
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={(engineConfig as DownloadEngineConfig).batch_size || 10}
                                    onChange={(e) => handleEngineConfigChange('batch_size', parseInt(e.target.value))}
                                />
                                <small>{t('experiments.form.engineConfig.download.batchSizeHelp')}</small>
                            </div>
                            <div className="form-group">
                                <label htmlFor="download_wait_time">
                                    {t('experiments.form.engineConfig.download.waitTime')}
                                </label>
                                <input
                                    id="download_wait_time"
                                    type="number"
                                    min="0"
                                    max="60000"
                                    step="100"
                                    value={(engineConfig as DownloadEngineConfig).wait_time || 1000}
                                    onChange={(e) => handleEngineConfigChange('wait_time', parseInt(e.target.value))}
                                />
                                <small>{t('experiments.form.engineConfig.download.waitTimeHelp')}</small>
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={(engineConfig as DownloadEngineConfig).retry_on_errors || false}
                                    onChange={(e) => handleEngineConfigChange('retry_on_errors', e.target.checked)}
                                />
                                <span>{t('experiments.form.engineConfig.download.retryOnErrors')}</span>
                            </label>
                            <small>{t('experiments.form.engineConfig.download.retryOnErrorsHelp')}</small>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // Loading state
    if (!authLoaded || loading) {
        return (
            <div className="loading-container">
                <div className="bonds-loader-flex">
                    <span className="material-icons loader-icon rotating">sync</span>
                    <span className="loader-text">
                        {isEditMode ? t('loading.experimentDetails') : t('loading.authentication')}
                    </span>
                </div>
            </div>
        );
    }

    // Access check
    if (user?.role !== 'ADMIN') {
        return (
            <div className="error-container">
                <div className="error-message">
                    <span className="material-icons" style={{ marginRight: '10px', color: '#e74c3c' }}>lock</span>
                    {t('experiments.accessDenied')}
                </div>
                <button onClick={handleBack} className="btn-primary" style={{ marginTop: '20px' }}>
                    <span className="material-icons" style={{ marginRight: '8px' }}>arrow_back</span>
                    {t('buttons.back')}
                </button>
            </div>
        );
    }

    return (
        <div className="experiment-create-edit-page">
            {/* Header */}
            <div className="experiment-form-header">
                <div className="experiment-form-nav">
                    <button onClick={handleBack} className="back-button">
                        <span className="material-icons">arrow_back</span>
                        {t('buttons.back')}
                    </button>
                </div>
                <h2>
                    <span className="material-icons">
                        {isEditMode ? 'edit' : 'add_circle'}
                    </span>
                    {isEditMode ? t('experiments.form.editTitle') : t('experiments.form.createTitle')}
                </h2>
            </div>

            {/* Error Display */}
            {error && (
                <div className="error-banner">
                    <span className="material-icons">error</span>
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="close-btn">
                        <span className="material-icons">close</span>
                    </button>
                </div>
            )}

            {/* Form */}
            <form className="experiment-form" onSubmit={(e) => { e.preventDefault(); handleSave(false); }}>
                {/* Basic Information Section */}
                <div className="form-section">
                    <h3>
                        <span className="material-icons">info</span>
                        {t('experiments.form.basicInfo')}
                    </h3>

                    <div className="form-group">
                        <label htmlFor="experiment_id">
                            {t('experiments.fields.experimentId')} <span className="required">*</span>
                        </label>
                        <input
                            id="experiment_id"
                            type="text"
                            value={formData.experiment_id}
                            onChange={(e) => handleChange('experiment_id', e.target.value)}
                            placeholder={t('experiments.form.experimentIdPlaceholder')}
                            className={validationErrors.experiment_id ? 'error' : ''}
                            disabled={isEditMode}
                        />
                        {validationErrors.experiment_id && (
                            <span className="error-message">{validationErrors.experiment_id}</span>
                        )}
                        <small>{t('experiments.form.experimentIdHelp')}</small>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="type">
                                {t('experiments.fields.type')} <span className="required">*</span>
                            </label>
                            <select
                                id="type"
                                value={formData.type}
                                onChange={(e) => handleChange('type', e.target.value)}
                                className={validationErrors.type ? 'error' : ''}
                            >
                                <option value="">{t('experiments.form.selectType')}</option>
                                {EXPERIMENT_TYPES.map(type => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                            {validationErrors.type && (
                                <span className="error-message">{validationErrors.type}</span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="tags">
                                {t('experiments.tags')}
                            </label>
                            <input
                                id="tags"
                                type="text"
                                value={tagsInput}
                                onChange={(e) => handleTagsChange(e.target.value)}
                                placeholder={t('experiments.form.tagsPlaceholder')}
                            />
                            <small>{t('experiments.form.tagsHelp')}</small>
                        </div>
                    </div>
                </div>

                {/* AI Configuration Section - Only for LLM-based experiments */}
                {isLLMExperimentType(formData.type) && (
                    <div className="form-section">
                        <h3>
                            <span className="material-icons">psychology</span>
                            {t('experiments.form.aiConfiguration')}
                        </h3>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="provider">
                                    {t('experiments.fields.provider')} <span className="required">*</span>
                                </label>
                                <select
                                    id="provider"
                                    value={formData.provider}
                                    onChange={(e) => handleProviderChange(e.target.value)}
                                    className={validationErrors.provider ? 'error' : ''}
                                >
                                    <option value="">{t('experiments.form.selectProvider')}</option>
                                    {PROVIDER_OPTIONS.map(provider => (
                                        <option key={provider} value={provider}>
                                            {provider.replace(/_/g, ' ').toUpperCase()}
                                        </option>
                                    ))}
                                </select>
                                {validationErrors.provider && (
                                    <span className="error-message">{validationErrors.provider}</span>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="model">
                                    {t('experiments.fields.model')} <span className="required">*</span>
                                </label>
                                <select
                                    id="model"
                                    value={formData.model}
                                    onChange={(e) => handleChange('model', e.target.value)}
                                    className={validationErrors.model ? 'error' : ''}
                                    disabled={!formData.provider}
                                >
                                    <option value="">{t('experiments.form.selectModel')}</option>
                                    {availableModels.map(model => (
                                        <option key={model} value={model}>
                                            {model}
                                        </option>
                                    ))}
                                </select>
                                {validationErrors.model && (
                                    <span className="error-message">{validationErrors.model}</span>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="endpoint">
                                {t('experiments.fields.endpoint')}
                            </label>
                            <input
                                id="endpoint"
                                type="url"
                                value={formData.endpoint || ''}
                                onChange={(e) => handleChange('endpoint', e.target.value)}
                                placeholder={t('experiments.form.endpointPlaceholder')}
                            />
                            <small>{t('experiments.form.endpointHelp')}</small>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="temperature">
                                    {t('experiments.form.temperature')}
                                </label>
                                <input
                                    id="temperature"
                                    type="number"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={formData.temperature || 0.7}
                                    onChange={(e) => handleChange('temperature', parseFloat(e.target.value))}
                                />
                                <small>{t('experiments.form.temperatureHelp')}</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="maxTokens">
                                    {t('experiments.form.maxTokens')}
                                </label>
                                <input
                                    id="maxTokens"
                                    type="number"
                                    min="256"
                                    max="128000"
                                    step="256"
                                    value={formData.maxTokens || 4096}
                                    onChange={(e) => handleChange('maxTokens', parseInt(e.target.value))}
                                />
                                <small>{t('experiments.form.maxTokensHelp')}</small>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="prompt">
                                {t('experiments.prompt')}
                            </label>
                            <textarea
                                id="prompt"
                                rows={6}
                                value={formData.prompt || ''}
                                onChange={(e) => handleChange('prompt', e.target.value)}
                                placeholder={t('experiments.form.promptPlaceholder')}
                            />
                            <small>{t('experiments.form.promptHelp')}</small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="json_schema">
                                {t('experiments.sections.jsonSchema')}
                            </label>
                            <textarea
                                id="json_schema"
                                rows={6}
                                value={formData.json_schema || ''}
                                onChange={(e) => handleChange('json_schema', e.target.value)}
                                placeholder={t('experiments.form.jsonSchemaPlaceholder')}
                                className="monospace"
                            />
                            <small>{t('experiments.form.jsonSchemaHelp')}</small>
                        </div>
                    </div>
                )}

                {/* Engine Configuration Section - Only for engine-based experiments */}
                {isEngineExperimentType(formData.type) && (
                    <div className="form-section engine-config-section">
                        {validationErrors.engineConfig && (
                            <div className="error-banner">
                                <span className="material-icons">error</span>
                                <span>{validationErrors.engineConfig}</span>
                            </div>
                        )}
                        {renderEngineConfig()}
                    </div>
                )}

                {/* Type-Specific Configuration - Only for LLM-based experiments */}
                {formData.type && isLLMExperimentType(formData.type) && (
                    <div className="form-section type-config-section">
                        <h3>
                            <span className="material-icons">{getExperimentTypeIcon(formData.type)}</span>
                            {t('experiments.form.typeConfiguration')}
                        </h3>
                        {renderTypeConfig()}
                    </div>
                )}

                {/* Form Actions */}
                <div className="form-actions">
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleBack}
                        disabled={saving}
                    >
                        <span className="material-icons">close</span>
                        {t('buttons.cancel')}
                    </button>
                    <div className="action-buttons">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => handleSave(true)}
                            disabled={saving}
                        >
                            <span className="material-icons">save</span>
                            {t('experiments.form.saveAsDraft')}
                        </button>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <span className="material-icons rotating">sync</span>
                                    {t('loading.processing')}
                                </>
                            ) : (
                                <>
                                    <span className="material-icons">check</span>
                                    {t('experiments.form.saveAndClose')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ExperimentCreateEdit;
